import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import Loader from '../../components/common/Loader';
import '../../styles/Forms.css';

// Order follows the role hierarchy: Admin > HOD > Manager > Team Leader > Employee.
const ROLE_COLUMNS = [
  { key: 'admin', label: 'Admin' },
  { key: 'hod', label: 'HOD' },
  { key: 'manager', label: 'Manager' },
  { key: 'teamLeader', label: 'Team Leader' },
  { key: 'employee', label: 'Employee' },
];

const FEATURE_MODULE_MAP = {
  add_assets: 'Assets',
  edit_assets: 'Assets',
  checkout: 'Assets',
  checkin: 'Assets',
  view_all_assets: 'Assets',
  view_assigned_assets: 'My Assets',
  delete_assets: 'Assets',

  add_team: 'Teams',
  edit_team: 'Teams',
  delete_team: 'Teams',
  show_all_teams: 'Teams',
  view_team_feedback: 'Teams',
  view_team_event_feedback: 'Teams',

  add_match: 'Matches',
  edit_match: 'Matches',
  give_match_tl_feedback: 'Matches',
  view_match_tl_feedback: 'Matches',
  give_supervisor_feedback: 'Matches',
  view_supervisor_feedback: 'Matches',
  show_all_matches: 'Matches',

  add_employee: 'People',
  edit_employee: 'People',
  delete_employee: 'People',
  manage_employee_permissions: 'People',
  view_asset_reports: 'Reports',
  view_performance_reports: 'Reports',

  view_my_asset_handover: 'Asset Handover',

  add_project: 'Projects',
  edit_project: 'Projects',
  delete_project: 'Projects',
  view_all_projects: 'Projects',
  add_task: 'Tasks',
  edit_task: 'Tasks',
  delete_task: 'Tasks',
  view_all_tasks: 'Tasks',
};

const MODULE_ORDER = ['Teams', 'Matches', 'Assets',  'Asset Handover','My Assets', 'People', 'Projects', 'Tasks', 'Reports', 'General'];
const FEATURE_ALIAS_MAP = {
  view_team_event_feedback: 'view_team_feedback',
};

/** When "Give …" is granted for a role, the paired "View …" is turned on if it was off. */
const GIVE_REQUIRES_VIEW_FEATURE = {
  give_match_tl_feedback: 'view_match_tl_feedback',
  give_supervisor_feedback: 'view_supervisor_feedback',
};

const UserRolesPage = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [moduleOpenState, setModuleOpenState] = useState({});

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const response = await api.getPermissions();
      if (response.success) {
        setPermissions(response.data || []);
        setInitialized(response.data && response.data.length > 0);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialize = async () => {
    try {
      setLoading(true);
      const response = await api.initializePermissions();
      if (response.success) {
        toast.success('Permissions initialized successfully!');
        fetchPermissions();
      }
    } catch (error) {
      console.error('Error initializing permissions:', error);
      toast.error('Error initializing permissions: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = (permissionId, role, value) => {
    setPermissions((prev) => {
      const changed = prev.find((p) => (p._id || p.id) === permissionId);
      const viewFeature =
        value && changed?.feature
          ? GIVE_REQUIRES_VIEW_FEATURE[changed.feature]
          : null;

      return prev.map((perm) => {
        const id = perm._id || perm.id;
        if (id === permissionId) {
          return {
            ...perm,
            roles: {
              ...perm.roles,
              [role]: value,
            },
          };
        }
        if (
          viewFeature &&
          perm.feature === viewFeature &&
          !perm.roles?.[role]
        ) {
          return {
            ...perm,
            roles: {
              ...perm.roles,
              [role]: true,
            },
          };
        }
        return perm;
      });
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.updatePermissions(permissions);
      if (response.success) {
        toast.success('Permissions updated successfully!');
        fetchPermissions();
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Error saving permissions: ' + (error.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const getFeatureLabel = (feature) => {
    const labels = {
      add_assets: 'Add Assets',
      edit_assets: 'Edit Assets',
      checkout: 'Check-Out',
      checkin: 'Check-In',
      add_employee: 'Add Employee',
      edit_employee: 'Edit Employee',
      delete_employee: 'Delete Employee',
      manage_employee_permissions: 'Manage Employee Permissions',
      view_all_assets: 'View All Assets',
      view_assigned_assets: 'View Assigned Assets',
      view_my_asset_handover: 'My Asset Handover',
      delete_assets: 'Delete Assets',
      view_asset_reports: 'Asset Reports',
      view_performance_reports: 'Performance Reports',
      add_team: 'Add Team',
      edit_team: 'Edit Team',
      delete_team: 'Delete Team',
      show_all_teams: 'Show All Teams',
      view_team_feedback: 'View Feedback',
      view_team_event_feedback: 'View Feedback',
      add_match: 'Add Match',
      edit_match: 'Edit Match',
      give_match_tl_feedback: 'Give TL Feedback',
      view_match_tl_feedback: 'View TL Feedback',
      give_supervisor_feedback: 'Give Supervisor Feedback',
      view_supervisor_feedback: 'View Supervisor Feedback',
      show_all_matches: 'Show All Matches',
      add_project: 'Add Project',
      edit_project: 'Edit Project',
      delete_project: 'Delete Project',
      view_all_projects: 'View All Projects',
      add_task: 'Add Task',
      edit_task: 'Edit Task',
      delete_task: 'Delete Task',
      view_all_tasks: 'View All Tasks',
    };
    return labels[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const getModuleLabel = (moduleName, feature) => {
    const featureKey = String(feature || '').trim();
    if (FEATURE_MODULE_MAP[featureKey]) return FEATURE_MODULE_MAP[featureKey];

    const normalizedModule = String(moduleName || '').trim();
    if (normalizedModule && normalizedModule.toLowerCase() !== 'general') return normalizedModule;

    if (featureKey.includes('team')) return 'Teams';
    if (featureKey.includes('employee')) return 'People';
    if (featureKey.includes('report')) return 'Reports';
    return 'Assets';
  };

  const uniquePermissions = Object.values(
    permissions.reduce((acc, permission) => {
      const feature = String(permission?.feature || '').trim();
      if (!feature || feature === 'submit_team_event_feedback') return acc;
      if (feature === 'view_team' || feature === 'view_matches') return acc;
      const moduleNameEarly = getModuleLabel(permission.module, permission.feature);
      if (String(moduleNameEarly).trim().toLowerCase() === 'admin') return acc;
      const canonicalFeature = FEATURE_ALIAS_MAP[feature] || feature;

      const existing = acc[canonicalFeature];
      if (!existing) {
        acc[canonicalFeature] = {
          ...permission,
          feature: canonicalFeature,
        };
        return acc;
      }

      const existingModule = getModuleLabel(existing.module, existing.feature);
      const nextModule = getModuleLabel(permission.module, permission.feature);
      if (existingModule === 'General' && nextModule !== 'General') {
        acc[canonicalFeature] = {
          ...permission,
          feature: canonicalFeature,
        };
      }
      return acc;
    }, {})
  );

  const groupedPermissions = uniquePermissions.reduce((acc, permission) => {
    if (permission?.feature === 'submit_team_event_feedback') {
      return acc;
    }
    const moduleName = getModuleLabel(permission.module, permission.feature);
    if (String(moduleName).trim().toLowerCase() === 'admin') return acc;
    if (!acc[moduleName]) acc[moduleName] = [];
    acc[moduleName].push(permission);
    return acc;
  }, {});

  const sortedModuleNames = Object.keys(groupedPermissions).sort((a, b) => {
    const aIndex = MODULE_ORDER.indexOf(a);
    const bIndex = MODULE_ORDER.indexOf(b);
    const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
    const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.localeCompare(b);
  });

  useEffect(() => {
    setModuleOpenState((prev) => {
      const next = {};
      sortedModuleNames.forEach((moduleName) => {
        next[moduleName] = prev[moduleName] ?? false;
      });
      return next;
    });
  }, [permissions]);

  const toggleModule = (moduleName) => {
    setModuleOpenState((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  };

  // True only when every module section is currently expanded.
  const allModulesOpen =
    sortedModuleNames.length > 0 && sortedModuleNames.every((moduleName) => moduleOpenState[moduleName]);

  // Expand or collapse all module sections at once.
  const setAllModules = (open) => {
    setModuleOpenState(() => {
      const next = {};
      sortedModuleNames.forEach((moduleName) => {
        next[moduleName] = open;
      });
      return next;
    });
  };

  if (loading && permissions.length === 0) {
    return <Loader />;
  }

  return (
    <div className="user-roles-page" style={{ padding: '16px' }}>
      {!initialized && (
        <div style={{
          background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>
              Permissions Not Initialized
            </div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>
              Click "Initialize Permissions" to set up default permissions
            </div>
          </div>
          <button
            onClick={handleInitialize}
            disabled={loading}
            style={{
              padding: '10px 20px',
              background: 'white',
              color: '#ed8936',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '13px',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Initializing...' : 'Initialize Permissions'}
          </button>
        </div>
      )}

      {permissions.length > 0 && (
        <>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Permission Management</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={() => setAllModules(!allModulesOpen)}
                  style={{
                    padding: '10px 16px',
                    background: '#fff',
                    color: '#4a5568',
                    border: '1px solid #cbd5e0',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <span style={{ fontSize: '12px' }}>{allModulesOpen ? '▲' : '▼'}</span>
                  {allModulesOpen ? 'Collapse All' : 'Expand All'}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                    fontSize: '13px',
                    opacity: saving ? 0.6 : 1
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '14px' }}>
              {sortedModuleNames.map((moduleName) => (
                  <div
                    key={moduleName}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleModule(moduleName)}
                      style={{
                        padding: '10px 12px',
                        background: '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                        fontWeight: 700,
                        color: '#2d3748',
                        fontSize: '13px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderTop: 'none',
                      }}
                    >
                      <span>{moduleName}</span>
                      <span style={{ fontSize: '14px', color: '#4a5568' }}>
                        {moduleOpenState[moduleName] ? '▼' : '▶'}
                      </span>
                    </button>
                    {moduleOpenState[moduleName] && <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '220px' }} />
                          <col style={{ width: '95px' }} />
                          <col style={{ width: '95px' }} />
                          <col style={{ width: '95px' }} />
                          <col style={{ width: '95px' }} />
                          <col style={{ width: '95px' }} />
                          <col />
                        </colgroup>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ textAlign: 'left', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Permission</th>
                            {ROLE_COLUMNS.map((role) => (
                              <th key={role.key} style={{ textAlign: 'center', padding: '10px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>
                                {role.label}
                              </th>
                            ))}
                            <th style={{ textAlign: 'left', padding: '10px', paddingLeft: '32px', fontSize: '11px', fontWeight: 600, color: '#718096' }}>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedPermissions[moduleName]
                            .sort((a, b) => getFeatureLabel(a.feature).localeCompare(getFeatureLabel(b.feature)))
                            .map((permission) => {
                              const permissionId = permission._id || permission.id;
                              return (
                                <tr key={permissionId} style={{ borderBottom: '1px solid #edf2f7' }}>
                                  <td style={{ padding: '10px', fontWeight: 600, color: '#2d3748', fontSize: '13px' }}>
                                    {getFeatureLabel(permission.feature)}
                                  </td>
                                  {ROLE_COLUMNS.map((role) => (
                                    <td key={role.key} style={{ padding: '10px', textAlign: 'center' }}>
                                      <input
                                        type="checkbox"
                                        checked={permission.roles?.[role.key] || false}
                                        onChange={(e) => handlePermissionChange(permissionId, role.key, e.target.checked)}
                                        style={{
                                          width: '18px',
                                          height: '18px',
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </td>
                                  ))}
                                  <td style={{ padding: '10px', paddingLeft: '32px', color: '#718096', fontSize: '13px', wordBreak: 'break-word' }}>
                                    {permission.description || '-'}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>}
                  </div>
                ))}
            </div>
          </div>

          <div style={{
            background: '#f7fafc',
            padding: '16px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>
              Permission Guidelines
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#4a5568', fontSize: '13px', lineHeight: '1.8' }}>
              <li>Check the boxes to grant permissions to specific roles</li>
              <li>Uncheck to revoke permissions</li>
              <li>Changes are saved when you click "Save Changes"</li>
              <li>Permission changes take effect immediately after saving</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default UserRolesPage;
